<template>
  <div class="body-landing">
    <AnimatedAiBackground />
    <div class="lang-switch" aria-label="Language switcher">
      <button
        type="button"
        class="lang-btn"
        :class="{ active: currentLocale === 'es' }"
        @click="setLocale('es')"
      >
        ES
      </button>
      <button
        type="button"
        class="lang-btn"
        :class="{ active: currentLocale === 'en' }"
        @click="setLocale('en')"
      >
        EN
      </button>
    </div>
    <!-- Barra de progreso -->
    <div class="progress-container">
      <div class="progress-bar" id="progressBar"></div>
    </div>

    <div id="globalLoading" class="loading-overlay hidden">
      <div class="spinner"></div>
    </div>
    <!-- Puntos de navegación -->
    <div class="nav-dots">
      <div class="nav-dot active" @click="safeScrollToSection(0)"></div>
      <div class="nav-dot" @click="safeScrollToSection(1)"></div>
      <div class="nav-dot" @click="safeScrollToSection(2)"></div>
      <div class="nav-dot" @click="safeScrollToSection(3)"></div>
      <div class="nav-dot" @click="safeScrollToSection(4)"></div>
      <div class="nav-dot" @click="safeScrollToSection(5)"></div>
    </div>

    <!-- Sección Start id="start"-->
    <section id="start" class="start">
      <div class="parallax" id="parallax-1"></div>
      <div class="hero-glow hero-glow-blue" aria-hidden="true"></div>
      <div class="hero-glow" aria-hidden="true"></div>

      <div class="content-landing hero-layout">
        <div class="hero-copy">
          <div class="hero-kicker animate">
            <span class="hero-kicker-dot"></span>
            Automatización de ventas por WhatsApp
          </div>
          <h1 class="animate delay-1">{{ $t("landing.hero.title") }}</h1>
          <p class="animate delay-2">
            {{ $t("landing.hero.subtitle") }}
          </p>
          <div class="btn-container animate delay-3">
            <a href="#contact" class="btn">{{
              $t("landing.hero.startFreeTrial")
            }}</a>
            <router-link to="/login" class="btn space-left">{{
              $t("landing.hero.login")
            }}</router-link>
          </div>
        </div>

        <div class="hero-demo animate delay-2">
          <SofiaChatDemo />
        </div>
      </div>
    </section>

    <!-- Sección Características -->
    <section id="features">
      <div class="parallax" id="parallax-2"></div>
      <div class="content-landing">
        <h2 class="animate">{{ $t("landing.features.title") }}</h2>
        <p class="animate delay-1">
          {{ $t("landing.features.subtitle") }}
        </p>
        <div class="features-grid">
          <div class="feature-card animate delay-1">
            <div class="feature-icon"><i class="fas fa-comments"></i></div>
            <h3>{{ $t("landing.features.chatAutomation") }}</h3>
            <p>
              {{ $t("landing.features.chatAutomationDescription") }}
            </p>
          </div>
          <div class="feature-card animate delay-2">
            <div class="feature-icon"><i class="fas fa-shopping-cart"></i></div>
            <h3>{{ $t("landing.features.sales247") }}</h3>
            <p>
              {{ $t("landing.features.sales247Description") }}
            </p>
          </div>
          <div class="feature-card animate delay-3">
            <div class="feature-icon"><i class="fas fa-chart-line"></i></div>
            <h3>{{ $t("landing.features.advancedAnalytics") }}</h3>
            <p>
              {{ $t("landing.features.advancedAnalyticsDescription") }}
            </p>
          </div>
          <div class="feature-card animate delay-4">
            <div class="feature-icon"><i class="fas fa-gears"></i></div>
            <h3>{{ $t("landing.features.simpleImplementation") }}</h3>
            <p>
              {{ $t("landing.features.simpleImplementationDescription") }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Sección ¿Cómo funciona? -->
    <section id="how-it-works">
      <div class="parallax" id="parallax-3"></div>
      <div class="content-landing">
        <h2 class="animate">{{ $t("landing.howItWorks.title") }}</h2>
        <p class="animate delay-1">
          {{ $t("landing.howItWorks.subtitle") }}
        </p>
        <div class="steps">
          <div class="step animate delay-1">
            <div class="step-number">1</div>
            <h3>{{ $t("landing.howItWorks.step1.title") }}</h3>
            <p>
              {{ $t("landing.howItWorks.step1.description") }}
            </p>
          </div>
          <div class="step animate delay-2">
            <div class="step-number">2</div>
            <h3>{{ $t("landing.howItWorks.step2.title") }}</h3>
            <p>{{ $t("landing.howItWorks.step2.description") }}</p>
          </div>
          <div class="step animate delay-3">
            <div class="step-number">3</div>
            <h3>{{ $t("landing.howItWorks.step3.title") }}</h3>
            <p>{{ $t("landing.howItWorks.step3.description") }}</p>
          </div>
          <div class="step step-video animate delay-4">
            <div class="step-number">4</div>
            <h3>{{ $t("landing.howItWorks.step4.title") }}</h3>
            <div class="step-video-frame">
              <video
                autoplay
                loop
                muted
                playsinline
                webkit-playsinline
                preload="metadata"
                :aria-label="$t('landing.howItWorks.step4.title')"
              >
                <source
                  src="/landing/videos/sofiabanner.mp4"
                  type="video/mp4"
                />
              </video>
              <div class="step-video-badge">
                <span></span>
                Demo
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Sección Precios -->
    <section id="pricing">
      <div class="parallax" id="parallax-4"></div>
      <div class="content-landing">
        <h2 class="animate">{{ $t("landing.pricing.title") }}</h2>
        <p class="animate delay-1">
          {{ $t("landing.pricing.subtitle") }}
        </p>
        <div id="plans" ref="plans" class="pricing-plans">
          <div
            v-for="(plan, idx) in plans"
            :key="plan.id"
            :class="['pricing-plan', 'animate', `delay-${idx + 1}`]"
          >
            <div class="plan-header">
              <h3 class="plan-name">{{ plan.name }}</h3>
              <div class="plan-price"><sup>$</sup>{{ plan.price }}</div>
              <div class="plan-period">por {{ plan.durationInDays }} días</div>
            </div>
            <div class="plan-features">
              <ul>
                <li v-for="(line, i) in getFeatures(plan.description)" :key="i">
                  {{ line }}
                </li>
              </ul>
              <a href="#contact" class="btn">{{
                $t("landing.pricing.startFreeTrial")
              }}</a>
            </div>
          </div>
          <p v-if="!plans.length && !loading" class="animate delay-2">
            {{ $t("landing.pricing.noPlans") }}
          </p>
        </div>
        <div class="pricing-currency-notice" role="note">
          <span class="pricing-currency-notice-icon" aria-hidden="true">€</span>
          <p>
            <strong>{{ $t("landing.pricing.currencyNoticeTitle") }}</strong>
            {{ $t("landing.pricing.currencyNoticeText") }}
          </p>
        </div>
      </div>
    </section>

    <!-- Sección Acerca de -->
    <section id="about">
      <div class="parallax" id="parallax-5"></div>
      <div class="content-landing">
        <h2 class="animate">{{ $t("landing.about.title") }}</h2>
        <div class="about-content">
          <div class="animate delay-1">
            <p>
              {{ $t("landing.about.description1") }}
            </p>
            <p>
              {{ $t("landing.about.description2") }}
            </p>
          </div>
          <div class="mission-card animate delay-1">
            <h3>{{ $t("landing.about.mission") }}</h3>
            <p>
              {{ $t("landing.about.missionDescription") }}
            </p>
          </div>
          <div class="values-container">
            <div class="value-card animate delay-1">
              <div class="value-icon"><i class="fas fa-lightbulb"></i></div>
              <h4>{{ $t("landing.about.innovation") }}</h4>
              <p>
                {{ $t("landing.about.innovationDescription") }}
              </p>
            </div>
            <div class="value-card animate delay-2">
              <div class="value-icon"><i class="fas fa-hands-helping"></i></div>
              <h4>{{ $t("landing.about.commitment") }}</h4>
              <p>{{ $t("landing.about.commitmentDescription") }}</p>
            </div>
            <div class="value-card animate delay-3">
              <div class="value-icon"><i class="fas fa-rocket"></i></div>
              <h4>{{ $t("landing.about.results") }}</h4>
              <p>{{ $t("landing.about.resultsDescription") }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Sección Contacto -->
    <section id="contact">
      <div class="parallax" id="parallax-6"></div>
      <div class="content-landing">
        <h2 class="animate">{{ $t("landing.contact.title") }}</h2>
        <p class="animate delay-1">
          {{ $t("landing.contact.subtitle") }}
        </p>
        <!-- Alertas estilizadas -->
        <div class="alert alert-success" id="successAlert">
          <i class="fas fa-check-circle alert-icon"></i>
          <div class="alert-content">
            <h4>{{ $t("landing.contact.successTitle") }}</h4>
            <p id="successMessage">
              {{ $t("landing.contact.successMessage") }}
            </p>
          </div>
        </div>
        <div class="alert alert-error" id="errorAlert">
          <i class="fas fa-exclamation-triangle alert-icon"></i>
          <div class="alert-content">
            <h4>{{ $t("landing.contact.errorTitle") }}</h4>
            <p id="errorMessage">
              {{ $t("landing.contact.errorMessage") }}
            </p>
          </div>
        </div>
        <div class="contact-form">
          <img class="sofia-logo-2" src="/landing/sofIA.svg" alt="Sofia" />
          <validation-observer ref="simpleRules">
            <b-form id="contactForm">
              <div class="form-group">
                <validation-provider
                  #default="{ errors }"
                  name="El nombre completo"
                  rules="required"
                >
                  <b-form-group
                    label-for="name"
                    :state="errors.length > 0 ? false : null"
                  >
                    <input
                      type="text"
                      id="name"
                      name="nombre"
                      v-model="model.nombre"
                      :placeholder="$t('landing.contact.namePlaceholder')"
                      required
                    />
                    <b-form-invalid-feedback
                      :state="errors.length > 0 ? false : null"
                    >
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="form-group">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('landing.contact.emailPlaceholder')"
                  rules="required"
                >
                  <b-form-group
                    label-for="email"
                    :state="errors.length > 0 ? false : null"
                  >
                    <input
                      type="email"
                      id="email"
                      name="correo"
                      v-model="model.correo"
                      :placeholder="$t('landing.contact.emailPlaceholder')"
                      required
                    />
                    <b-form-invalid-feedback
                      :state="errors.length > 0 ? false : null"
                    >
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="form-group">
                <b-form-group label-for="phone">
                  <validation-provider
                    #default="{ errors }"
                    name="Teléfono"
                    rules=""
                  >
                    <vue-tel-input
                      id="phone"
                      inputId="telefono"
                      name="phone"
                      v-model="model.telefono"
                      defaultCountry="ve"
                      :disabledFetchingCountry="true"
                      :mode="'international'"
                      class="custom-tel"
                      :inputOptions="inputTelOption"
                      @open="onDropdownOpen(true)"
                      @close="onDropdownOpen(false)"
                    >
                      <template v-slot:arrow-icon>
                        <span>{{ openTelInput ? "▲" : "▼" }}</span>
                      </template>
                    </vue-tel-input>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </div>
              <div class="form-group">
                <input
                  type="text"
                  id="nombreEmpresa"
                  name="nombreEmpresa"
                  v-model="model.nombreEmpresa"
                  :placeholder="$t('landing.contact.businessNamePlaceholder')"
                />
              </div>
              <div class="form-group">
                <textarea
                  id="mensaje"
                  name="mensaje"
                  v-model="model.mensaje"
                  :placeholder="$t('landing.contact.messagePlaceholder2')"
                  rows="5"
                  required
                ></textarea>
              </div>
              <button
                id="save"
                type="submit"
                @click.prevent="onSubmit"
                class="form-btn btn-submit"
              >
                {{ $t("landing.contact.sendMessage") }}
              </button>
            </b-form>
          </validation-observer>
        </div>
      </div>
    </section>

    <footer class="main-footer">
      <!--Widgets Section-->
      <div class="widgets-section">
        <div class="auto-container">
          <div class="row">
            <!--Footer Column-->
            <div class="footer-column">
              <div class="footer-widget about-widget">
                <h3 class="widget-title" style="z-index: 10">SOFIA</h3>
                <a class="center-logo" href="#start">
                  <div class="logo-container">
                    <div class="logo" ref="logo">
                      <div class="logo-inner">
                        <img
                          style="width: 55px"
                          src="/landing/log-sof-icon.png"
                          alt="Sofia"
                        />
                      </div>
                    </div>
                    <!-- <ParticlesLayer :amount="150" /> -->
                  </div>
                </a>
                <ul class="social-icon-two">
                  <li>
                    <a
                      href="https://www.linkedin.com/company/smart-sales-agent/"
                      target="_blank"
                    >
                      <i class="fab fa-linkedin"></i>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.instagram.com/sofia_agente?igsh=MTIzdjBmcjVxbmk3eQ=="
                      target="_blank"
                    >
                      <i class="fab fa-instagram"></i>
                    </a>
                  </li>
                  <li>
                    <a href="https://wa.me/584145038466" target="_blank">
                      <i class="fab fa-whatsapp"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <!--Footer Column-->
            <div class="footer-column">
              <div class="footer-widget">
                <h3 class="widget-title">
                  {{ $t("landing.footer.explore") }}
                </h3>
                <ul class="user-links">
                  <li>
                    <a href="#start">
                      {{ $t("landing.footer.home") }}
                    </a>
                  </li>
                  <li>
                    <a href="#features">{{ $t("landing.footer.features") }}</a>
                  </li>
                  <li>
                    <a href="#how-it-works">{{
                      $t("landing.footer.howItWorks")
                    }}</a>
                  </li>
                  <li>
                    <a href="#pricing">{{ $t("landing.footer.pricing") }}</a>
                  </li>
                  <li>
                    <a href="#about">{{ $t("landing.footer.about") }}</a>
                  </li>
                  <li>
                    <a href="#contact">{{ $t("landing.footer.contact") }}</a>
                  </li>
                </ul>
              </div>
            </div>
            <!--Footer Column-->
            <div class="footer-column">
              <div class="footer-widget contact-widget">
                <h3 class="widget-title">{{ $t("landing.footer.contact") }}</h3>
                <div class="widget-content">
                  <ul class="contact-info">
                    <li>
                      <i class="fa fa-envelope"></i>
                      <a href="mailto:contacto@agentesofia.com"
                        >contacto@agentesofia.com</a
                      >
                    </li>
                    <li>
                      <i class="fa fa-phone-square"></i>
                      <a href="tel:+584145038466">+58 414-5038466</a>
                    </li>
                    <li>
                      <i class="fab fa-whatsapp"></i>
                      <a href="https://wa.me/584145038466">WhatsApp</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <!--Footer Column-->
            <div class="footer-column">
              <div class="footer-widget">
                <h3 class="widget-title">
                  {{ $t("landing.footer.legalDocuments") }}
                </h3>
                <ul class="user-links">
                  <li>
                    <router-link to="/privacy">{{
                      $t("landing.footer.privacyPolicy")
                    }}</router-link>
                  </li>
                  <li>
                    <router-link to="/terms">{{
                      $t("landing.footer.termsAndConditions")
                    }}</router-link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--Footer Bottom-->
      <div class="footer-bottom">
        <div class="auto-container">
          <div class="inner-container">
            <div class="copyright-text">
              © Copyright 2025 por
              <a href="#start">{{ $t("landing.footer.sofiaAgent") }}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
// import ParticlesLayer from "./ParticlesLayer.vue";
import { required } from "@validations";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import { BForm, BFormGroup, BFormInvalidFeedback } from "bootstrap-vue";
import AnimatedAiBackground from "./AnimatedAiBackground.vue";
import SofiaChatDemo from "./SofiaChatDemo.vue";
// import Chatbot from "./Chatbot.vue";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";

const domain =
  process.env.VUE_APP_PRODUCTION == "true"
    ? process.env.VUE_APP_API_URL_PROD
    : process.env.VUE_APP_API_URL_DEV;

const model = {
  nombre: null,
  correo: null,
  telefono: "",
  nombreEmpresa: null,
  mensaje: null,
};

export default {
  name: "Landing",
  components: {
    // ParticlesLayer,
    AnimatedAiBackground,
    SofiaChatDemo,
    ValidationProvider,
    BForm,
    BFormGroup,
    ValidationObserver,
    BFormInvalidFeedback,
    // Chatbot,
    VueTelInput,
  },
  data() {
    return {
      isSave: true,
      plans: [],
      required,
      loading: false,
      iti: null,
      model: Object.assign(model),
      inputTelOption: {
        placeholder: "Ingrese un número de teléfono",
      },
      openTelInput: false,
      sectionsSel: [
        "#start",
        "#features",
        "#how-it-works",
        "#pricing",
        "#about",
        "#contact",
      ],
      _ioReveal: null,
      _scrollHandler: null,
      _px: { els: [], ticking: false, disabled: false, raf: null },
    };
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale || "es";
    },
  },

  watch: {
    "$i18n.locale"(locale) {
      localize(locale);
    },
  },

  async created() {
    localize(this.$i18n.locale);
  },

  mounted() {
    this.initParallax();
    this.initObservers();
    this.handlePageNavigation();
    this.initScrollEvents();
    window.addEventListener("scroll", this.onScroll.bind(this), {
      passive: true,
    });
    window.addEventListener("resize", this.onResize, { passive: true });
    this.loadPlans();
    this.scrollToRouteHash();
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);

    if (this._scrollHandler)
      window.removeEventListener("scroll", this._scrollHandler);
    if (this._ioReveal) this._ioReveal.disconnect();
  },

  methods: {
    scrollToRouteHash() {
      const hash = this.$route.hash;
      if (!hash) return;

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const target = document.querySelector(hash);
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });
    },

    setLocale(locale) {
      this.$i18n.locale = locale;

      try {
        localStorage.setItem("locale", locale);
      } catch (error) {
        // ignore
      }

      localize(locale);
    },

    onDropdownOpen(isOpen) {
      this.openTelInput = isOpen;
    },

    // ---- Aparición progresiva con IntersectionObserver
    initObservers() {
      const opts = {
        root: null,
        threshold: 0.35,
        rootMargin: "0px 0px -15% 0px",
      };
      this._ioReveal = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            e.target.querySelectorAll(".animate").forEach((el) => {
              el.style.animationPlayState = "running";
            });
          }
        });
      }, opts);

      document
        .querySelectorAll("section")
        .forEach((sec) => this._ioReveal.observe(sec));
    },

    // ---- Barra de progreso y dots activos (throttle para no saturar)
    initScrollProgress() {
      const progressBar = document.getElementById("progressBar");
      const navDots = document.querySelectorAll(".nav-dot");
      const sections = this.sectionsSel.map((sel) =>
        document.querySelector(sel),
      );

      this._scrollHandler = this.throttle(() => {
        const y = window.scrollY;
        const h = document.documentElement.scrollHeight - window.innerHeight;
        progressBar.style.width = (y / h) * 100 + "%";

        let active = sections.findIndex(
          (s) =>
            y >= s.offsetTop - window.innerHeight * 0.3 &&
            y < s.offsetTop + s.offsetHeight - window.innerHeight * 0.3,
        );
        if (active === -1) active = 0;
        navDots.forEach((d, i) => d.classList.toggle("active", i === active));
      }, 80);

      window.addEventListener("scroll", this._scrollHandler, { passive: true });
    },

    throttle(fn, wait) {
      let last = 0,
        t;
      return (...args) => {
        const now = Date.now();
        const remaining = wait - (now - last);
        if (remaining <= 0) {
          last = now;
          fn.apply(this, args);
        } else if (!t) {
          t = setTimeout(() => {
            last = Date.now();
            t = null;
            fn.apply(this, args);
          }, remaining);
        }
      };
    },

    // ---- Scroll seguro a la sección
    safeScrollToSection(idx) {
      const target = document.querySelector(this.sectionsSel[idx]);
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top, behavior: "smooth" });
    },

    // Métodos migrados del script.js
    createParallax(containerId, count) {
      const container = document.getElementById(containerId);
      if (!container) return;
      for (let i = 0; i < count; i++) {
        const item = document.createElement("div");
        item.className = "parallax-item";
        const size = Math.random() * 200 + 50;
        item.style.width = `${size}px`;
        item.style.height = `${size}px`;
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        item.style.left = `${left}%`;
        item.style.top = `${top}%`;
        const opacity = Math.random() * 0.3 + 0.1;
        item.style.opacity = opacity;
        container.appendChild(item);
      }
    },

    handlePageNavigation() {
      // Activar animaciones solo en index.html
      this.$nextTick(() => {
        document.querySelectorAll("#start .animate").forEach((el) => {
          el.style.animationPlayState = "running";
        });
      });
    },

    initVideo() {
      this.$nextTick(() => {
        const video = document.getElementById("heroVideo");
        const iosOverlay = document.getElementById("iosOverlay");
        const activateBtn = document.getElementById("activateVideoBtn");
        if (!video) return;
        const isIOS =
          /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              iosOverlay.classList.add("hidden");
            })
            .catch((error) => {
              if (isIOS) {
                iosOverlay.classList.remove("hidden");
              }
              console.log("Autoplay fallido:", error);
            });
        }
        if (activateBtn) {
          activateBtn.addEventListener("click", () => {
            video
              .play()
              .then(() => {
                iosOverlay.classList.add("hidden");
                video.muted = false;
              })
              .catch((e) => console.error("Error al activar video:", e));
          });
        }
      });
    },

    initParallax() {
      // Crear efectos parallax en cada sección
      this.$nextTick(() => {
        this.createParallax("parallax-1", 8);
        this.createParallax("parallax-2", 6);
        this.createParallax("parallax-3", 7);
        this.createParallax("parallax-4", 5);
        this.createParallax("parallax-5", 6);
        this.createParallax("parallax-6", 6);
      });
    },

    onScroll() {
      if (!this._px || this._px.disabled || this._px.ticking) return;
      this._px.ticking = true;
      this._px.raf = requestAnimationFrame(() => {
        this.updateParallax(false);
        this._px.ticking = false;
      });
    },

    onResize() {
      // Recalcula en resize para corregir posiciones
      this.initParallax();
    },

    updateParallax(force) {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const vh = window.innerHeight;

      this._px.els.forEach((obj) => {
        const rect = obj.el.getBoundingClientRect();
        // Distancia del centro de viewport al centro del elemento
        const elementCenter = rect.top + scrollY + rect.height / 2;
        const viewportCenter = scrollY + vh / 2;
        const delta = elementCenter - viewportCenter;

        // Movemos en sentido contrario para que parezca más lento (ajusta signo si lo quieres al revés)
        const translate = -delta * obj.speed;

        // Solo aplicamos si está en viewport o si es la primera vez (force)
        if (force || (rect.bottom > 0 && rect.top < vh)) {
          obj.el.style.transform = `translate3d(0, ${translate}px, 0)`;
        }
      });
    },

    initScrollEvents() {
      this.$nextTick(() => {
        const sections = document.querySelectorAll("section");
        const navDots = document.querySelectorAll(".nav-dot");
        const progressBar = document.getElementById("progressBar");
        navDots.forEach((dot, index) => {
          dot.addEventListener("click", () => this.safeScrollToSection(index));
        });
        window.addEventListener("scroll", () => {
          if (!sections || sections.length === 0) return;
          const currentScroll = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight =
            document.documentElement.scrollHeight - windowHeight;
          const scrollPercent = (currentScroll / documentHeight) * 100;
          progressBar.style.width = `${scrollPercent}%`;
          sections.forEach((section, index) => {
            const sectionTop = section.offsetTop;
            const sectionHeight = section.offsetHeight;
            if (
              currentScroll >= sectionTop - windowHeight * 0.3 &&
              currentScroll < sectionTop + sectionHeight - windowHeight * 0.3
            ) {
              navDots.forEach((dot) => dot.classList.remove("active"));
              navDots[index].classList.add("active");
              const animatedElements = section.querySelectorAll(".animate");
              animatedElements.forEach((el) => {
                el.style.animationPlayState = "running";
              });
            }
          });
        });
        window.addEventListener("mousemove", (e) => {
          const x = e.clientX / window.innerWidth;
          const y = e.clientY / window.innerHeight;
          document.querySelectorAll(".parallax-item").forEach((item) => {
            const speed = parseFloat(item.style.opacity) * 2;
            const moveX = (0.5 - x) * 30 * speed;
            const moveY = (0.5 - y) * 30 * speed;
            item.style.transform = `translate(${moveX}px, ${moveY}px)`;
          });
        });
      });
    },

    getParams(arrayParams = []) {
      const searchParams = new URLSearchParams();
      const arr = Array.isArray(arrayParams) ? arrayParams : [];
      arr.forEach((param) => {
        const split = param.split(",");
        if (split.length === 2) {
          searchParams.append(split[0].trim(), split[1].trim());
        }
      });
      return searchParams;
    },

    getFeatures(desc) {
      return desc.split(/\r?\n/).filter((line) => line.trim() !== "");
    },

    async loadPlans() {
      this.$nextTick(async () => {
        const loadingOverlay = document.getElementById("globalLoading");
        const plansContainer = this.$refs.plans;
        loadingOverlay.classList.remove("hidden");
        this.loading = true;
        try {
          const arrParams = [];
          arrParams.push(`page,${1}`);
          arrParams.push(`rows,${999}`);
          const param = this.getParams(arrParams);
          const res = await fetch(`${domain}/Subscriptions/GetList?${param}`);
          const resp = await res.json();
          if (!res.ok) {
            const errorData = resp.message;
            this.alertToast("danger", "Error", errorData, "AlertTriangleIcon");
            return;
          }
          this.plans =
            resp.data && Array.isArray(resp.data.items) ? resp.data.items : [];
        } catch (e) {
          console.error(e);
          this.alertToast(
            "danger",
            "Error",
            "Se produjo un error",
            "AlertTriangleIcon",
          );
          // plansContainer.innerHTML = `<p class="animate delay-2 error">Error al cargar los planes. Intenta más tarde.</p>`;
        } finally {
          loadingOverlay.classList.add("hidden");
          this.loading = false;
        }
      });
    },

    onSubmit() {
      this.$refs.simpleRules.validate().then(async (success) => {
        if (success) {
          if (!this.model.telefono) {
            this.alertToast(
              "danger",
              "Alerta",
              `${this.$t("landing.contact.phoneRequired")}`,
            );
            return;
          }
          // const successAlert = document.getElementById("successAlert");
          // const errorAlert = document.getElementById("errorAlert");

          // this.isSave = true;
          const globalLoading = document.getElementById("globalLoading");
          globalLoading.classList.remove("hidden");
          const formData = {
            nombre: this.model.nombre,
            correo: this.model.correo,
            telefono: this.model.telefono,
            nombreEmpresa: this.model.nombreEmpresa || "No proporcionado",
            mensaje: this.model.mensaje,
          };

          try {
            const res = await fetch(`${domain}/SmartSalesAgent/Registro`, {
              method: "POST",
              headers: {
                accept: "*/*",
                "Content-Type": "application/json",
              },
              body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error("Error en la petición");

            // successAlert.textContent =
            //   "¡Mensaje enviado! Te contactaremos pronto.";
            // successAlert.classList.add("show");
            this.$refs.simpleRules.reset();
            this.model = Object.assign({
              nombre: null,
              correo: null,
              telefono: "",
              nombreEmpresa: null,
              mensaje: null,
            });

            this.alertToast(
              "success",
              "¡Mensaje enviado!",
              "Te contactaremos pronto.",
            );
          } catch (err) {
            console.error(err);
            this.alertToast(
              "danger",
              "Error",
              `${this.$t("landing.contact.sendErrorMessage")}`,
              "AlertTriangleIcon",
            );

            // errorAlert.textContent =
            //   "No pudimos enviar tu mensaje. Por favor intenta más tarde.";
            // errorAlert.classList.add("show");
          } finally {
            globalLoading.classList.add("hidden");
            // this.isSave = false;
          }
        } else {
          // setInterval(() => {
          //   document.querySelectorAll(".alert.show").forEach((alert) => {
          //     alert.classList.remove("show");
          //   });
          // }, 8000);
          this.alertToast(
            "danger",
            "Error",
            `${this.$t("landing.contact.fillAllFields")}`,
            "AlertTriangleIcon",
          );
        }
      });
    },

    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>
<style lang="css">
.content-landing {
  max-width: 1200px;
  min-height: 0 !important;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}
.title {
  font-size: 3rem;
  margin-bottom: 1rem;
}
.animate {
  animation: fadeInUp 1s ease forwards;
  animation-play-state: paused;
  opacity: 0;
}

.sofia-logo-2 {
  position: absolute;
  /* left: -45%;
  width: 400px; */
  left: -30%;
  width: 200px;
}

.logo {
  position: absolute;
  animation: pulse 6s infinite ease-in-out;
  filter: drop-shadow(0 0 10px #00c8ff);
  transform-style: preserve-3d;
  transition: transform 1.5s ease;
  /* transform: translate(-50%, -50%); */
}

/* Animaciones */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Logo */
@keyframes pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
    opacity: 0.8;
  }
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

@keyframes typing {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

@keyframes blink {
  50% {
    border-color: transparent;
  }
}

.iti {
  width: 100%;
}

.tel-input input {
  border: 1px solid #ccc;
  padding: 8px;
  font-size: 14px;
  border-radius: 8px;
}

.custom-tel {
  width: 100%;
  height: 50px;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.custom-tel .vue-tel-input {
  /* background: #2a2a2a; */
  /* border: 1px solid #4f4e4e; */
  border-radius: 8px;
}

.custom-tel .vti__input {
  border: none;
  background: #4e506000;
  color: #fff;
}
.custom-tel .vue-tel-input input {
  color: #fff;
  background: transparent;
}

.custom-tel .vue-tel-input:focus-within {
  border-color: #1976d2ff;
}

.custom-tel .vti__dropdown {
  background-color: transparent;
}

.custom-tel .vti__dropdown:hover,
.custom-tel .vti__dropdown.open,
.custom-tel .vti__dropdown.disabled {
  background-color: transparent;
}

.custom-tel .vti__dropdown-list {
  background-color: #2a2a2a;
  border: 1px solid #4f4e4e;
}

.custom-tel .vti__dropdown-item {
  color: #fff;
}

.custom-tel .vti__dropdown-item.highlighted {
  background-color: #373737;
}

.custom-tel .vti__selection .vti__country-code {
  color: #fff;
}

.custom-tel .vti__search_box {
  background: #2a2a2a;
  border: 1px solid #4f4e4e;
  color: #fff;
}

/* Placeholder más tenue, si quieres */
.vue-tel-input input::placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
}
/* Para inputs dentro de iti */
.vue-tel-input input::placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* WebKit (Chrome, Safari, Opera) */
#telefono input::-webkit-input-placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Mozilla Firefox 19+ */
#telefono input::-moz-placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
  opacity: 1; /* Firefox reduce opacidad por defecto */
}

/* Mozilla Firefox 4 – 18 */
#telefono input:-moz-placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
  opacity: 1;
}

/* Internet Explorer 10+ */
#telefono input:-ms-input-placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Ajustes sobre la bandera para que se vea bien */
#telefono__selected-flag {
  color: #fff;
}

#telefono__country-list {
  color: #000;
  background: #fff;
}

/* 1) Estilos para el input de archivo */
.custom-file-input {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #bbb;
  border-radius: 4px;
  background-color: #fafafa;
  cursor: pointer;
}

/* 3) Sobrescribiendo el dropdown de vue-tel-input */
.custom-tel-input .vue-tel-input__dropdown {
  background-color: #e6f7ff !important; /* fondo claro azul */
}

/* 4) Ajustes al campo de teléfono */
.custom-tel-input input {
  width: 100%; /* ocupa todo el ancho */
  padding: 8px 12px;
  font-size: 1rem;
  color: #333;
  border: 1px solid #bbb;
  border-radius: 4px;
}

/* 5) Color del borde de la bandera seleccionada */
.custom-tel-input .selected-flag {
  border-color: #1890ff;
}
</style>

<style lang="scss" scoped>
.body-landing {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif !important;
  // background: linear-gradient(135deg, #052e6d, #0a4a9f);
  color: #ffffff;
  overflow-x: hidden;
}

#start {
  background: none !important;
}

.start {
  position: relative;
  min-height: 100vh;
  height: auto;
  display: flex;
  align-items: center;
  overflow: hidden;
  z-index: 999;
  background: linear-gradient(
      115deg,
      rgba(2, 11, 30, 0.96),
      rgba(5, 35, 78, 0.78)
    ),
    radial-gradient(
      circle at 70% 35%,
      rgba(40, 169, 224, 0.18),
      transparent 45%
    ) !important;
}

.hero-layout {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
  align-items: center;
  gap: clamp(40px, 7vw, 96px);
}

.hero-copy {
  max-width: 650px;
}

.hero-copy h1 {
  max-width: 650px;
  margin-bottom: 24px;
  font-size: clamp(3rem, 5.4vw, 5.4rem);
  line-height: 0.98;
  letter-spacing: -0.045em;
  text-wrap: balance;
  text-shadow: 0 16px 50px rgba(0, 0, 0, 0.35);
}

.hero-copy > p {
  max-width: 600px;
  margin-bottom: 32px;
  color: rgba(235, 244, 255, 0.82);
  font-size: clamp(1.08rem, 1.55vw, 1.35rem);
  line-height: 1.65;
}

.hero-kicker {
  width: fit-content;
  margin-bottom: 24px;
  padding: 8px 14px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(135, 201, 255, 0.22);
  border-radius: 999px;
  color: #c7e7ff;
  background: rgba(9, 38, 75, 0.58);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.hero-kicker-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #25d366;
  box-shadow: 0 0 0 5px rgba(37, 211, 102, 0.12);
  animation: hero-status-pulse 2s ease-in-out infinite;
}

.hero-copy .btn-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.hero-copy .btn {
  min-width: 180px;
  margin: 0;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  box-shadow: 0 15px 34px rgba(0, 0, 0, 0.2);
}

.hero-copy .btn:first-child {
  border-color: transparent;
  background: linear-gradient(135deg, #28a9e0, #126fbe);
}

.hero-copy .btn:first-child:hover {
  box-shadow: 0 18px 40px rgba(40, 169, 224, 0.3);
}

.hero-copy .space-left {
  margin-left: 0;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
}

.hero-demo {
  position: relative;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(45px);
  opacity: 0.65;
}

.hero-glow-blue {
  width: 520px;
  height: 520px;
  top: -180px;
  left: -180px;
  background: radial-gradient(
    circle,
    rgba(40, 169, 224, 0.27),
    transparent 70%
  );
}

.hero-glow-green {
  width: 420px;
  height: 420px;
  right: -160px;
  bottom: -180px;
  background: radial-gradient(
    circle,
    rgba(37, 211, 102, 0.17),
    transparent 70%
  );
}

@keyframes hero-status-pulse {
  50% {
    box-shadow: 0 0 0 9px rgba(37, 211, 102, 0);
  }
}

#features {
  // background: #0a4a9f;
}

#how-it-works {
  // background: #052e6d;
}

#pricing {
  // background: #0a4a9f;
}

#about {
  // background: #052e6d;
}

#testimonials {
  // background: #052e6d;
}

#contact-form {
  // background: #052e6d;
}

.progress-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 1000;
}

.progress-bar {
  height: 5px;
  background: linear-gradient(90deg, #28a9e0, #1a7bbd);
  width: 0%;
  transition: width 0.3s ease;
}

.nav-dots {
  position: fixed;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.nav-dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
}

.nav-dot.active {
  background: #28a9e0;
  transform: scale(1.3);
}

.nav-dot::after {
  content: attr(data-section);
  position: absolute;
  left: -135px;
  top: 50%;
  transform: translateY(-50%);
  /* color: white; */
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
}

.nav-dot:hover::after {
  opacity: 1;
}

section {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  /* padding: 50px; */
  padding: 100px 50px 80px;
  position: relative;
  overflow: hidden;
  /* transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1); */
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

h1,
h2,
h3,
h4,
h5,
h6,
.h1,
.h2,
.h3,
.h4,
.h5,
.h6 {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif !important;
  color: #ffffff;
}

h1 {
  font-size: 5rem;
  margin-bottom: 20px;
  line-height: 1.1;
  font-weight: 800;
  text-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

h2 {
  font-size: 2.5rem;
  margin-bottom: 30px;
  font-weight: 700;
  position: relative;
}

h2:after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 60px;
  height: 4px;
  background: #28a9e0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

h3 {
  font-weight: bold;
}

.widget-title {
  font-size: 1.5rem;
  margin-bottom: 25px;
  position: relative;
  padding-bottom: 15px;
}

.widget-title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 30px;
  height: 2px;
  background: #28a9e0;
}

p {
  font-size: 1.5rem;
  line-height: 1.8;
  max-width: 700px;
  margin-bottom: 30px !important;
}

.btn {
  display: inline-block;
  // padding: 15px 45px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #28a9e0, #1a7bbd);
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  margin-top: 20px;
  /* transition: all 0.3s ease; */
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  border: none;
  cursor: pointer;
  text-align: center;
  // height: 45px;
}

.btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
}

.btn-group {
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
}

.space-left {
  margin-left: 5px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.feature-card {
  min-height: 300px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.feature-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  color: #28a9e0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.feature-card:hover .feature-icon {
  background: transparent;
  color: #ffffff;
  transform: rotate(15deg) scale(1.1);
}

.steps {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 50px;
}

.step {
  flex: 1;
  min-width: 250px;
  max-width: 300px;
  text-align: center;
  position: relative;
  padding: 40px 30px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.step.animated {
  transform: translateY(0);
  opacity: 1;
}

.step:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.step-number {
  width: 60px;
  height: 60px;
  background: #052e6d;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 20px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.step:hover .step-number {
  background: #28a9e0;
  transform: scale(1.1);
}

.step-video {
  padding-right: 20px;
  padding-left: 20px;
}

.step-video-frame {
  position: relative;
  width: 100%;
  margin-top: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  aspect-ratio: 16 / 10;
  background: #020b1c;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.22);
}

.step-video-frame video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.step-video-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 5px 9px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  color: #fff;
  background: rgba(2, 11, 28, 0.72);
  backdrop-filter: blur(8px);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-video-badge span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #25d366;
  box-shadow: 0 0 0 4px rgba(37, 211, 102, 0.15);
}

.pricing-plans {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  /* margin-top: 50px; */
}

.pricing-currency-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 720px;
  margin: 28px auto 0;
  padding: 12px 16px;
  border: 1px solid rgba(115, 190, 235, 0.35);
  border-radius: 10px;
  background: rgba(4, 22, 48, 0.92);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  text-align: left;
}

.pricing-currency-notice-icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(115, 190, 235, 0.2);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
}

#pricing .pricing-currency-notice p {
  margin: 0 !important;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.83rem;
  line-height: 1.5;
}

#pricing .pricing-currency-notice strong {
  display: block;
  margin-bottom: 2px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
}

@media (max-width: 575px) {
  .pricing-currency-notice {
    margin-top: 22px;
    padding: 12px;
  }
}

.pricing-plan {
  flex: 1;
  min-width: 300px;
  max-width: 350px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s ease;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  flex-direction: column;
}

.pricing-plan:hover {
  transform: translateY(-15px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.plan-header {
  padding: 30px;
  text-align: center;
  background: rgba(0, 0, 0, 0.2);
}

.plan-name {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.plan-price {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.plan-period {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
}

.plan-features {
  padding: 30px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.plan-features ul {
  flex-grow: 1; /* Ocupa todo el espacio disponible */
  margin-bottom: 20px; /* Espacio antes del botón */
}

.plan-features li {
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  /* text-align: center; */
}
.plan-features .btn {
  margin-top: auto; /* Empuja el botón hacia abajo */
  width: 90%; /* Ancho consistente */
  max-width: 200px; /* Ancho máximo */
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  padding: 12px 10px; /* Padding consistente */
}
.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  /* margin-top: 50px; */
}

.testimonial-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.testimonial-card:hover {
  transform: translateY(-5px);
}

.testimonial-content {
  font-style: italic;
  margin-bottom: 20px;
  position: relative;
  padding-left: 30px;
}

.testimonial-content::before {
  content: "";
  position: absolute;
  left: 0;
  top: -20px;
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.2);
  font-family: Georgia, serif;
}

.testimonial-author {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.author-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.author-info h4 {
  margin-bottom: 5px;
}

.author-info p {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0;
  font-size: 1rem;
}

.delay-1 {
  animation-delay: 0.2s;
}

.delay-2 {
  animation-delay: 0.4s;
}

.delay-3 {
  animation-delay: 0.6s;
}

.delay-4 {
  animation-delay: 0.8s;
}

.delay-5 {
  animation-delay: 1s;
}

.delay-6 {
  animation-delay: 1.2s;
}

/* Efecto parallax */
.parallax {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

.parallax-item {
  position: absolute;
  border-radius: 50%;
  transition: transform 0.1s linear;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 70%
  );
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.fade-in.visible {
  opacity: 1;
  transform: translateY(0);
}

.video-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-object-fit: cover;
  object-position: center center;
  z-index: -2;
}

.start .overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: -1;
}

/* Contact Section */
.contact-section {
  background: linear-gradient(135deg, #052e6d, #1a7bbd);
  color: #fff;
}

.contact-section h2 {
  color: #fff;
}

.contact-section h2:after {
  background: #fff;
}

.contact-section p {
  color: rgba(255, 255, 255, 0.9);
}

.contact-form {
  max-width: 600px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(10px);
}

input:focus,
textarea:focus {
  border-color: linear-gradient(135deg, #0a4a9f, #1a7bbd);
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #ffffff;
  font-size: 1rem;
}

.form-group textarea {
  min-height: 150px;
  resize: vertical;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.form-btn {
  width: 100%;
  background: #ffffff;
  color: #052e6d;
  font-weight: 700;
  padding: 16px;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.form-btn:hover {
  background: #eef2f6;
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
}

.btn-submit {
  display: inline-block;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-size: 1.1rem;
  border: none;
  outline: none;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.btn-submit:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #f0f7ff;
  z-index: -1;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.btn-submit:hover:before {
  transform: scale(1.05);
  opacity: 0.9;
}

/* Footer */
.main-footer {
  position: relative;
  background: linear-gradient(
    145deg,
    rgba(2, 13, 34, 0.98),
    rgba(5, 46, 109, 0.96)
  );
  color: #ffffff;
  padding: 80px 0 0;
  overflow: hidden;
  margin-top: auto;
  border-top: 1px solid rgba(79, 195, 247, 0.22);
  box-shadow: 0 -24px 60px rgba(0, 0, 0, 0.22);
}

.main-footer .bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  opacity: 0.1;
}

.widgets-section {
  position: relative;
  z-index: 2;
}

.auto-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

.row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
}

.footer-column {
  padding: 0 15px;
  margin-bottom: 40px;
  flex: 1 1 220px;
  min-width: 220px;
}

.footer-widget {
  margin-bottom: 40px;
}

/* .about-widget .logo {
    margin-bottom: 20px;
}

.about-widget .logo img {
    max-width: 200px;
} */

.about-widget .text {
  margin-bottom: 25px;
  line-height: 1.7;
  font-size: 1rem;
  max-width: 350px;
}

.social-icon-two {
  display: flex;
  gap: 15px;
  list-style: none;
  padding: 0;
  justify-content: center;
}

.social-icon-two a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  font-size: 18px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.social-icon-two a:hover {
  background: #28a9e0;
  transform: translateY(-3px);
}

.user-links {
  list-style: none;
  padding: 0;
}

.user-links li {
  margin-bottom: 12px;
}

.user-links li a {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-size: 1.05rem;
}

.user-links li a:hover {
  color: #28a9e0;
  padding-left: 5px;
}

.contact-info {
  list-style: none;
  padding: 0;
}

.contact-info li {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
}

.contact-info li i {
  font-size: 1.2rem;
  margin-right: 15px;
  color: #28a9e0;
  min-width: 20px;
}

.contact-info li a {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.contact-info li a:hover {
  color: #28a9e0;
}

.footer-bottom {
  position: relative;
  background: rgba(0, 7, 24, 0.5);
  z-index: 999;
  padding: 25px 0;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.copyright-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
}

.copyright-text a {
  color: #28a9e0;
  text-decoration: none;
}

/* Diseño del logo SofIA */
.logo-container {
  position: relative;
  margin: 0 auto 40px;
  width: 4rem;
  height: 4rem;
  perspective: 1000px;
}

.logo-inner {
  position: absolute;
  // width: 70%;
  // height: 70%;
  // background: rgba(255, 255, 255, 0.1);
  // border: 2px solid rgba(255, 255, 255, 0.2);
  // border-radius: 50%;
  // display: flex;
  // justify-content: center;
  // align-items: center;
  backdrop-filter: blur(5px);
}

.ios-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  color: white;
  text-align: center;
  padding: 20px;
}

.ios-overlay.hidden {
  display: none;
}

.overlay-content {
  max-width: 80%;
}

#activateVideoBtn {
  background: #4e54c8;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 1rem;
  margin-top: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

#activateVideoBtn i {
  margin-right: 8px;
}

.video-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
}

.btn-container {
  display: block;
}

.about-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 50px;
  max-width: 1200px;
  margin: 0 auto;
}

.about-content {
  flex: 1;
  min-width: 300px;
  text-align: left;
  padding: 30px;
  background: transparent;
  border-radius: 20px;
  border: 0px solid rgba(255, 255, 255, 0.1);
}

.about-content p {
  margin-bottom: 25px;
  font-size: 1.1rem;
  line-height: 1.8;
  min-width: 100%;
}

.mission-card {
  /* background: linear-gradient(45deg, #4e54c8, #8f94fb); */
  // background: linear-gradient(135deg, #0a4a9f, #1a7bbd);
  /* background: transparent; */
  padding: 30px;
  border-radius: 0px;
  margin-top: 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  z-index: 1;
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 0px solid rgba(255, 255, 255, 0.1);
}

.mission-card:before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  z-index: -1;
  animation: rotate 15s linear infinite;
}

.mission-card p {
  min-width: 100%;
  text-align: center;
}

.mission-card h3 {
  font-size: 1.8rem;
  margin-bottom: 20px;
}

.values-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 50px;
}

.value-card {
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 15px;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.value-card:hover {
  transform: translateY(-10px);
  background: rgba(78, 84, 200, 0.2);
}

.value-icon {
  font-size: 2.5rem;
  margin-bottom: 20px;
  /* color: #8f94fb; */
}

.value-card h4 {
  font-size: 1.3rem;
  margin-bottom: 15px;
  /* color: #8f94fb; */
}

.center-logo {
  justify-content: start;
  display: flex;
}

/* .loading {
  display: none;
  text-align: center;
  margin: 20px 0;
} */

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #4e54c8;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

.required {
  color: #ff6b6b;
}

.api-info {
  background: rgba(10, 15, 35, 0.7);
  border-radius: 12px;
  padding: 20px;
  margin-top: 30px;
  font-family: monospace;
  font-size: 0.9rem;
  overflow-x: auto;
}

.api-info h3 {
  margin-bottom: 15px;
  color: #8f94fb;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Alertas estilizadas */
.alert {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 20px 20px;
  border-radius: 12px;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  max-width: 400px;
  transform: translateX(120%);
  transition: transform 0.4s ease;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.alert.show {
  transform: translateX(0);
}

.alert-success {
  background: rgba(40, 167, 69, 0.9);
}

.alert-error {
  background: rgba(220, 53, 69, 0.9);
}

.alert-info {
  background: rgba(23, 162, 184, 0.9);
}

.alert-icon {
  font-size: 28px;
}

.alert-content h4 {
  font-size: 1.2rem;
  margin-bottom: 5px;
}

.alert-content p {
  font-size: 1rem;
  line-height: 1.5;
}

.close-alert {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  margin-left: auto;
  padding: 0 5px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-overlay.hidden {
  display: none;
}

.loading-overlay .spinner {
  border: 4px solid #ccc;
  border-top-color: #3880ff;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 992px) {
  .hero-layout {
    grid-template-columns: minmax(0, 1fr) minmax(300px, 0.82fr);
    gap: 30px;
  }

  .hero-copy h1 {
    font-size: clamp(2.8rem, 6vw, 4.2rem);
  }

  h1 {
    font-size: 3rem;
  }

  h2 {
    font-size: 2.2rem;
  }

  section {
    padding: 80px 30px 60px;
  }

  .btn-group {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 300px;
    margin: 10px 0;
  }

  .footer-column {
    flex: 0 0 50%;
  }

  .social-icon-two {
    justify-content: start;
  }

  .logo-container {
    margin: 0 0 40px;
  }

  .space-left {
    margin-left: 5px;
  }
}

@media (max-width: 768px) {
  .start {
    padding-top: 95px;
    padding-bottom: 90px;
  }

  .hero-layout {
    grid-template-columns: 1fr;
    gap: 52px;
  }

  .hero-copy {
    max-width: 620px;
    margin: 0 auto;
    text-align: center;
  }

  .hero-copy h1,
  .hero-copy > p {
    margin-right: auto;
    margin-left: auto;
  }

  .hero-kicker {
    margin-right: auto;
    margin-left: auto;
  }

  .hero-copy .btn-container {
    display: flex;
    justify-content: center;
  }

  .hero-copy .btn {
    width: auto;
    min-width: 180px;
  }

  /* h1 {
        font-size: 3rem;
    }

    h2 {
        font-size: 2rem;
    } */

  /* .nav-dots {
        right: 15px;
    } */

  /* section {
        padding: 30px 20px;
    } */

  /* .content {
        text-align: center;
    } */

  /* p {
        margin-left: auto;
        margin-right: auto;
    } */

  /* .feature-card, .pricing-plan {
        min-height: auto;
    } */

  h1 {
    font-size: 2.5rem;
  }

  h2 {
    font-size: 2rem;
  }

  p {
    font-size: 1.1rem;
  }

  .nav-dots {
    top: auto;
    bottom: 20px;
    right: 50%;
    transform: translateX(50%);
    flex-direction: row;
    gap: 8px;
  }

  .nav-dot {
    width: 12px;
    height: 12px;
  }

  .section-header {
    margin-bottom: 40px;
  }

  .nav-dot::after {
    display: none;
  }

  /* .logo {
        max-width: 140px;
    } */

  .step,
  .pricing-plan {
    min-width: 100%;
    max-width: 100%;
  }

  .contact-form {
    padding: 25px;
  }

  .footer-column {
    flex: 0 0 100%;
  }

  .social-icon-two {
    justify-content: start;
  }

  .logo-container {
    margin: 0 0 40px;
  }

  .btn-container {
    display: grid;
    justify-content: center;
  }

  .about-container {
    flex-direction: column;
  }

  .about-content {
    /* text-align: center; */
  }

  .about-content h2:after {
    left: 50%;
    transform: translateX(-50%);
  }

  .mission-card {
    padding: 20px;
  }

  .space-left {
    margin-left: 0px;
  }
  .sofia-logo-2 {
    width: 100px;
    left: -3rem;
    top: 10rem;
    z-index: -999;
  }
}

@media (max-width: 480px) {
  .start {
    padding-top: 82px;
  }

  .hero-copy h1 {
    font-size: clamp(2.45rem, 12vw, 3.25rem);
  }

  .hero-kicker {
    padding: 7px 10px;
    font-size: 0.65rem;
  }

  .hero-copy .btn-container {
    display: grid;
    width: 100%;
  }

  .hero-copy .btn {
    width: 100%;
    max-width: 320px;
  }

  section {
    padding: 70px 20px 50px;
  }

  h1 {
    font-size: 2.2rem;
  }

  h2 {
    font-size: 1.8rem;
  }

  .feature-card,
  .testimonial-card {
    padding: 20px;
  }

  .nav-dots {
    top: auto;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    flex-direction: row;
    gap: 8px;
  }

  .nav-dot {
    width: 12px;
    height: 12px;
  }

  .social-icon-two {
    justify-content: center;
  }

  .logo-container {
    margin: 0 0 40px;
  }

  .user-links li {
    text-align: center;
  }

  .contact-info li {
    justify-content: center;
  }

  .center-logo {
    justify-content: center;
    display: flex;
  }
  .space-left {
    margin-left: 0px;
  }

  .sofia-logo-2 {
    width: 100px;
    left: -3rem;
    top: 10rem;
    z-index: -999;
  }
}

.lang-switch {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1100;
  display: flex;
  gap: 8px;
}

.lang-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-btn:hover {
  background: rgba(255, 255, 255, 0.16);
}

.lang-btn.active {
  background: #fff;
  color: #0a4a9f;
  border-color: #fff;
}

@media (prefers-reduced-motion: reduce) {
  .hero-kicker-dot {
    animation: none;
  }
}
</style>
